import { useState } from "react";
import { Form, Row, Col } from "react-bootstrap";
import CountryPicker from "@components/Forms/CountryPicker";
import type { VendorFormValues } from "../types";

export interface VendorFormProps {
  id: string;
  initialValues: VendorFormValues;
  isSubmitting: boolean;
  onSubmit: (values: VendorFormValues) => void;
}

export const VendorForm = ({
  id,
  initialValues,
  isSubmitting,
  onSubmit,
}: VendorFormProps): JSX.Element => {
  const [values, setValues] = useState<VendorFormValues>(initialValues);

  const set = <K extends keyof VendorFormValues>(
    key: K,
    v: VendorFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: v }));

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    onSubmit(values);
  };

  return (
    <Form id={id} data-testid={id} onSubmit={handleSubmit}>
      <Form.Group className="mb-3">
        <Form.Label htmlFor="vendor-name">Name</Form.Label>
        <Form.Control
          id="vendor-name"
          type="text"
          required
          value={values.name}
          onChange={(e) => set("name", e.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label htmlFor="vendor-street-address">Street Address</Form.Label>
        <Form.Control
          id="vendor-street-address"
          type="text"
          value={values.streetAddress}
          onChange={(e) => set("streetAddress", e.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label htmlFor="vendor-street2">Address Line 2</Form.Label>
        <Form.Control
          id="vendor-street2"
          type="text"
          value={values.street2}
          onChange={(e) => set("street2", e.target.value)}
        />
      </Form.Group>

      <Row>
        <Col sm={3}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="vendor-city">City</Form.Label>
            <Form.Control
              id="vendor-city"
              type="text"
              value={values.city}
              onChange={(e) => set("city", e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col sm={3}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="vendor-state">St/Prov/Terr</Form.Label>
            <Form.Control
              id="vendor-state"
              type="text"
              value={values.state}
              onChange={(e) => set("state", e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col sm={3}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="vendor-postal">Zip/Post</Form.Label>
            <Form.Control
              id="vendor-postal"
              type="text"
              value={values.postalCode}
              onChange={(e) => set("postalCode", e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col sm={3}>
          <Form.Group className="mb-3">
            <Form.Label>Country</Form.Label>
            <CountryPicker
              country={values.country || null}
              onCountryChange={(c: VendorFormValues["country"]) =>
                set("country", c)
              }
            />
          </Form.Group>
        </Col>
      </Row>
    </Form>
  );
};

export default VendorForm;
